/**
 * AppLayout — Portal v2 app shell (DEC-063, QFP-derived design).
 *
 *   ┌─────────────────────────────────────────────────┐
 *   │ AppBar (48px) — logo + (optional) WorkspaceTabs │
 *   ├─────────────────────────────────────────────────┤
 *   │ SectionSubTabs (40px)  ← only inside a section  │
 *   ├───────────────┬─────────────────────────────────┤
 *   │               │ Breadcrumbs                     │
 *   │   Drawer      │ <KeepAliveOutlet />             │
 *   │   (280px,     │                                 │
 *   │   collapsible)│                                 │
 *   └───────────────┴─────────────────────────────────┘
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import KeepAliveOutlet from './KeepAliveOutlet';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Menu as MenuIcon, ChevronLeft as ChevronLeftIcon } from 'lucide-react';
import { Navigation } from '../navigation/Navigation';
import { UserMenu } from '../navigation/UserMenu';
import { Breadcrumbs } from '../navigation/Breadcrumbs';
import { SectionSubTabs } from '../navigation/SectionSubTabs';
import { CommandPalette } from '../navigation/CommandPalette';
import { WorkspaceTabs } from '../navigation/WorkspaceTabs';
import {
  WorkspaceTabsProvider,
  isWorkspaceTabsEnabled,
} from '../navigation/workspaceTabsStore';
import { PORTAL_META } from '../registry/portalMeta';

const DRAWER_WIDTH = 280;
const APPBAR_HEIGHT = 48;

const AppLayoutInner: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const tabsEnabled = isWorkspaceTabsEnabled();

  const handleDrawerToggle = () => setDrawerOpen((o) => !o);
  const handleNavigateInDrawer = () => {
    if (isMobile) setDrawerOpen(false);
  };

  // Publish drawer width as a CSS variable so full-bleed fixed children can
  // offset past the persistent sidebar.
  useEffect(() => {
    const px = drawerOpen && !isMobile ? `${DRAWER_WIDTH}px` : '0px';
    document.documentElement.style.setProperty('--portal-drawer-px', px);
  }, [drawerOpen, isMobile]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CommandPalette />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ minHeight: `${APPBAR_HEIGHT}px !important`, px: 2, gap: 1 }}>
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            aria-label={drawerOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            {drawerOpen ? <ChevronLeftIcon size={18} /> : <MenuIcon size={18} />}
          </IconButton>

          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 0.5,
                bgcolor: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  color: 'primary.contrastText',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  fontFamily: '"JetBrains Mono", monospace',
                }}
              >
                {PORTAL_META.platformShort}
              </Typography>
            </Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: theme.palette.text.primary }}>
              {PORTAL_META.platformTitle}
            </Typography>
          </Box>

          {/* Optional inline workspace-tabs bar — opt-in via VITE_WORKSPACE_TABS=true */}
          {tabsEnabled && (
            <Box sx={{ flex: 1, mx: 2, overflow: 'hidden' }}>
              <WorkspaceTabs />
            </Box>
          )}

          {!tabsEnabled && <Box sx={{ flexGrow: 1 }} />}

          <UserMenu />
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={isMobile ? handleDrawerToggle : undefined}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            top: APPBAR_HEIGHT,
            height: `calc(100% - ${APPBAR_HEIGHT}px)`,
          },
        }}
      >
        <Navigation onNavigate={handleNavigateInDrawer} />
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0, // prevent flex overflow from long tables
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          marginLeft: drawerOpen && !isMobile ? 0 : `-${DRAWER_WIDTH}px`,
          mt: `${APPBAR_HEIGHT}px`,
        }}
      >
        <SectionSubTabs />

        <Box sx={{ p: 3 }}>
          <Breadcrumbs />
          <KeepAliveOutlet />
        </Box>
      </Box>
    </Box>
  );
};

export const AppLayout: React.FC = () => (
  <WorkspaceTabsProvider>
    <AppLayoutInner />
  </WorkspaceTabsProvider>
);

export default AppLayout;
